/**
 * Combobox - Searchable dropdown with create option
 *
 * A lightweight combobox built on Radix Popover (no cmdk dependency)
 * Supports search filtering, keyboard navigation, and "create new" option
 */

import * as React from 'react';
import { Check, ChevronsUpDown, Plus, Search, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';

export interface ComboboxOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  isLoading?: boolean;
  allowCreate?: boolean;
  onCreateNew?: (searchValue: string) => void;
  createLabel?: string;
  className?: string;
  triggerClassName?: string;
}

export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = 'Select option...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
  disabled = false,
  isLoading = false,
  allowCreate = false,
  onCreateNew,
  createLabel = 'Create new',
  className,
  triggerClassName,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  // Filter options based on search
  const filteredOptions = React.useMemo(() => {
    if (!search) return options;
    const searchLower = search.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(searchLower) ||
        option.description?.toLowerCase().includes(searchLower)
    );
  }, [options, search]);

  // Check if search matches any existing option exactly
  const hasExactMatch = React.useMemo(() => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return options.some((opt) => opt.label.toLowerCase() === searchLower);
  }, [options, search]);

  // Show create option when search has no exact match
  const showCreateOption = allowCreate && search && !hasExactMatch && onCreateNew;

  // Get selected option label
  const selectedLabel = React.useMemo(() => {
    const selected = options.find((opt) => opt.value === value);
    return selected?.label;
  }, [options, value]);

  // Handle keyboard navigation
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      const totalItems = filteredOptions.length + (showCreateOption ? 1 : 0);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev + 1) % totalItems);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev - 1 + totalItems) % totalItems);
          break;
        case 'Enter':
          e.preventDefault();
          if (showCreateOption && highlightedIndex === filteredOptions.length) {
            onCreateNew?.(search);
            setOpen(false);
            setSearch('');
          } else if (filteredOptions[highlightedIndex]) {
            const option = filteredOptions[highlightedIndex];
            if (!option.disabled) {
              onValueChange(option.value);
              setOpen(false);
              setSearch('');
            }
          }
          break;
        case 'Escape':
          setOpen(false);
          setSearch('');
          break;
      }
    },
    [filteredOptions, highlightedIndex, showCreateOption, search, onCreateNew, onValueChange]
  );

  // Reset highlighted index when search changes
  React.useEffect(() => {
    setHighlightedIndex(0);
  }, [search]);

  // Focus input when popover opens
  React.useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Scroll highlighted item into view
  React.useEffect(() => {
    if (listRef.current) {
      const highlighted = listRef.current.querySelector('[data-highlighted="true"]');
      highlighted?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoading}
          className={cn(
            'w-full justify-between font-normal',
            !value && 'text-muted-foreground',
            triggerClassName
          )}
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </span>
          ) : selectedLabel ? (
            <span className="truncate">{selectedLabel}</span>
          ) : (
            placeholder
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('w-[--radix-popover-trigger-width] p-0', className)}
        align="start"
      >
        {/* Search Input */}
        <div className="flex items-center border-b px-3 py-2">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <Input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
            className="h-8 border-0 bg-transparent p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>

        {/* Options List */}
        <ScrollArea className="max-h-[300px]">
          <div ref={listRef} className="p-1">
            {filteredOptions.length === 0 && !showCreateOption ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {emptyMessage}
              </div>
            ) : (
              <>
                {filteredOptions.map((option, index) => (
                  <div
                    key={option.value}
                    data-highlighted={index === highlightedIndex}
                    className={cn(
                      'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
                      index === highlightedIndex && 'bg-accent text-accent-foreground',
                      option.disabled && 'pointer-events-none opacity-50',
                      option.value === value && 'font-medium'
                    )}
                    onClick={() => {
                      if (!option.disabled) {
                        onValueChange(option.value);
                        setOpen(false);
                        setSearch('');
                      }
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        option.value === value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <div className="flex flex-col">
                      <span>{option.label}</span>
                      {option.description && (
                        <span className="text-xs text-muted-foreground">
                          {option.description}
                        </span>
                      )}
                    </div>
                  </div>
                ))}

                {/* Create New Option */}
                {showCreateOption && (
                  <div
                    data-highlighted={highlightedIndex === filteredOptions.length}
                    className={cn(
                      'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
                      highlightedIndex === filteredOptions.length &&
                        'bg-accent text-accent-foreground',
                      'border-t mt-1 pt-2'
                    )}
                    onClick={() => {
                      onCreateNew?.(search);
                      setOpen(false);
                      setSearch('');
                    }}
                    onMouseEnter={() => setHighlightedIndex(filteredOptions.length)}
                  >
                    <Plus className="mr-2 h-4 w-4 text-primary" />
                    <span className="text-primary">
                      {createLabel}: "{search}"
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
